import React from 'react';
import ThemedButton from '../Shared/ThemedButton';
import { isDarkMode } from '../../utility/themeVars';
import { formatDateTime } from '../../utility/bookingUtils';
import { getOriginalTimeDisplay } from '../../utility/timezoneUtils';

const RescheduleWorkflowSidebar = ({ 
  bookingPage, 
  appointment,
  selectedDate = null,
  selectedTime = null,
  isAvailableSlot = false,
  customTimeRequested = false,
  onSubmitReschedule,
  className = '',
  disabled = false,
  isSubmitting = false,
  primaryColor = 'blue-600',
  themeMode = 'light'
}) => {
  const isDark = isDarkMode(themeMode);

  // Helper function to determine if appointment requires approval
  const requiresApproval = () => {
    return customTimeRequested || !isAvailableSlot || (bookingPage && bookingPage.autoApproval === false);
  };

  // Format the original appointment time
  const formatOriginalTime = () => {
    if (!appointment?.startTime) return 'N/A';
    
    // Use timezone-aware formatting with 12-hour format
    const originalTimeDisplay = getOriginalTimeDisplay(
      appointment.startTime,
      appointment.timeZoneId,
      true // Always use 12-hour format
    );
    
    // If no timezone difference, format using local time with 12-hour format
    if (!originalTimeDisplay) {
      const date = new Date(appointment.startTime);
      return date.toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      });
    }
    
    return originalTimeDisplay;
  };

  // Format the new appointment time
  const formatNewTime = () => {
    if (!selectedDate || !selectedTime) return '—';
    return formatDateTime(selectedDate, selectedTime);
  };

  // Check if reschedule can be submitted
  const canSubmit = selectedDate && selectedTime && !disabled && !isSubmitting;

  const businessName = bookingPage?.businessName || 'this business';

  return (
    <div className={`space-y-6 w-full ${className} lg:sticky lg:top-6 lg:self-start`}>
      {/* Reschedule Summary Card */}
      <div className="bg-themed-card border-themed rounded-card shadow-hairline border p-6">
        <div>
          {/* Step indicator */}
          <div className="mb-4 flex items-center space-x-2 text-xs text-themed-secondary smallcaps tracking-wider" aria-hidden="true">
            <span className={!selectedDate || !selectedTime ? "text-themed-primary" : ""}>
              1 · Pick a time
            </span>
            <span>→</span>
            <span className={selectedDate && selectedTime ? "text-themed-primary" : ""}>
              2 · Confirm
            </span>
          </div>

          {/* Time Comparison Section */}
          <div className="mb-6">
            <h3 className="font-display text-lg text-themed-primary mb-4">
              Reschedule details
            </h3>

            <div className="space-y-4">
              {/* Previous Time */}
              <div>
                <div className="smallcaps text-themed-secondary mb-1">
                  Previous time
                </div>
                <div className="text-sm text-themed-secondary line-through tabular">
                  {formatOriginalTime()}
                </div>
              </div>

              {/* New Time */}
              <div>
                <div className="smallcaps text-themed-secondary mb-1">
                  New time
                </div>
                <div className={`text-sm font-medium tabular ${selectedDate && selectedTime ? 'text-themed-primary' : 'text-themed-secondary'}`}>
                  {formatNewTime()}
                </div>
              </div>
            </div>
          </div>

          {/* Approval Status Indicator */}
          {selectedDate && selectedTime && (
            <div className="mb-4">
              {requiresApproval() ? (
                <div className={`${isDark ? 'bg-signal-amber-500/20 border-signal-amber-500' : 'bg-signal-amber-50 border-signal-amber-100'} border rounded-card p-3`}>
                  <div className="flex items-start space-x-2">
                    <svg
                      aria-hidden="true"
                      className={`w-5 h-5 ${isDark ? 'text-signal-amber-100' : 'text-signal-amber-600'} mt-0.5 flex-shrink-0`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isDark ? 'text-signal-amber-100' : 'text-signal-amber-600'}`}>
                        Reschedule needs confirmation
                      </p>
                      <p className={`text-xs ${isDark ? 'text-signal-amber-100' : 'text-signal-amber-600'} mt-1`}>
                        {customTimeRequested
                          ? `You requested a custom time — ${businessName} will review and email you.`
                          : `${businessName} will review and email you.`}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`${isDark ? 'bg-signal-green-500/20 border-signal-green-500' : 'bg-signal-green-50 border-signal-green-100'} border rounded-card p-3`}>
                  <div className="flex items-start space-x-2">
                    <svg
                      aria-hidden="true"
                      className={`w-5 h-5 ${isDark ? 'text-signal-green-100' : 'text-signal-green-600'} mt-0.5 flex-shrink-0`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isDark ? 'text-signal-green-100' : 'text-signal-green-600'}`}>
                        Your new time is available
                      </p>
                      <p className={`text-xs ${isDark ? 'text-signal-green-100' : 'text-signal-green-600'} mt-1`}>
                        We'll email a confirmation right away.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <ThemedButton
            onClick={onSubmitReschedule}
            primaryColor={primaryColor}
            themeMode={themeMode}
            size="large"
            className="w-full"
            disabled={!canSubmit}
          >
            {isSubmitting
              ? 'Submitting...'
              : requiresApproval()
                ? 'Submit reschedule request'
                : 'Confirm reschedule'
            }
          </ThemedButton>
        </div>
      </div>
    </div>
  );
};

export default RescheduleWorkflowSidebar;